import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Loader2, Send, X } from 'lucide-react';
import { askMeetingAssistant, countReadyOrgDocuments } from '../../utils/orgDocuments';
import { getQuorumStatus } from '../../utils/quorumUtils';

const SOURCE_OPTIONS = [
  { value: 'both', label: 'Both' },
  { value: 'org', label: 'Our documents' },
  { value: 'general', label: 'General procedure' },
];

function motionSummary(currentMotion, motionTypes) {
  if (!currentMotion) return 'None pending';
  const name = motionTypes?.[currentMotion.type]?.name || currentMotion.type;
  const text = String(currentMotion.text || '').slice(0, 120);
  return `${name}${text ? `: ${text}` : ''}`;
}

export default function MeetingAssistantCard({
  organizationId,
  orgSettingsPath = null,
  meetingSetup,
  currentMotion,
  motionTypes,
  agendaTitle = null,
  theme = 'light',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [sources, setSources] = useState('both');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [readyCount, setReadyCount] = useState(null);
  const inputRef = useRef(null);

  const cardClass =
    theme === 'dark'
      ? 'bg-gray-800 text-white border border-gray-600'
      : 'bg-pp-surface text-pp-ink border border-pp-line/80';

  const refreshReadyCount = useCallback(async () => {
    if (!organizationId) {
      setReadyCount(0);
      return;
    }
    try {
      const n = await countReadyOrgDocuments(organizationId);
      setReadyCount(n);
    } catch {
      setReadyCount(0);
    }
  }, [organizationId]);

  useEffect(() => {
    void refreshReadyCount();
  }, [refreshReadyCount]);

  useEffect(() => {
    const focus = () => setIsOpen(true);
    window.addEventListener('pp-focus-meeting-assistant', focus);
    return () => window.removeEventListener('pp-focus-meeting-assistant', focus);
  }, []);

  // Focus the question field once the modal has actually mounted.
  useEffect(() => {
    if (!isOpen) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  const submit = async () => {
    const q = question.trim();
    if (!q || !organizationId || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const quorum = getQuorumStatus(meetingSetup || {});
      const data = await askMeetingAssistant({
        organizationId,
        question: q,
        sources,
        meetingContext: {
          organizationName: meetingSetup?.organizationName || '',
          agendaTitle: agendaTitle || '',
          currentMotionSummary: motionSummary(currentMotion, motionTypes),
          quorumPresent: Boolean(quorum?.present),
        },
      });
      setResult(data);
      void refreshReadyCount();
    } catch (err) {
      setError(err.message || 'Assistant failed');
    } finally {
      setBusy(false);
    }
  };

  const noOrgDocs = readyCount === 0;

  if (!isOpen) {
    return (
      <button
        type="button"
        id="meeting-assistant-card"
        data-tour="meeting-assistant"
        onClick={() => setIsOpen(true)}
        className={`${cardClass} flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left shadow-pp hover:opacity-90`}
      >
        <BookOpen className="h-4 w-4 shrink-0 text-pp-accent" aria-hidden />
        <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-pp-ink'}`}>
          Ask the assistant
        </span>
        {result && (
          <span className={`ml-auto text-[11px] ${theme === 'dark' ? 'text-gray-400' : 'text-pp-muted'}`}>
            Last answer available
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        id="meeting-assistant-card"
        data-tour="meeting-assistant"
        className={`${cardClass} max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl p-4 shadow-xl`}
      >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-pp-accent" aria-hidden />
          <div>
            <h3 className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-pp-ink'}`}>
              Ask the assistant
            </h3>
            <p className={`mt-0.5 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-pp-muted'}`}>
              Bylaws excerpts and general RONR-style practice. Does not change meeting buttons.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className={`shrink-0 rounded p-1 ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-pp-muted hover:text-pp-ink'}`}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-2 flex flex-wrap gap-1.5">
        {SOURCE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setSources(opt.value)}
            className={`rounded-lg px-2 py-1 text-[11px] font-medium transition-colors ${
              sources === opt.value
                ? 'bg-pp-accent text-white'
                : theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  : 'bg-pp-paper text-pp-muted hover:bg-pp-line/40'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {noOrgDocs && (sources === 'both' || sources === 'org') && (
        <p className={`mb-2 text-xs ${theme === 'dark' ? 'text-amber-200' : 'text-amber-800'}`}>
          No bylaws uploaded yet — answers use general practice only.
          {orgSettingsPath ? (
            <>
              {' '}
              <Link to={orgSettingsPath} className="font-medium underline underline-offset-2">
                Upload in Org settings
              </Link>
            </>
          ) : null}
        </p>
      )}

      <textarea
        ref={inputRef}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void submit();
          }
        }}
        rows={2}
        placeholder="Ask about our bylaws or general procedure…"
        className={`mb-2 w-full resize-y rounded-lg border px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-pp-accent/30 ${
          theme === 'dark'
            ? 'border-gray-600 bg-gray-900 text-white'
            : 'border-pp-line bg-white text-pp-ink'
        }`}
        disabled={busy || !organizationId}
      />

      <button
        type="button"
        disabled={busy || !question.trim() || !organizationId}
        onClick={() => void submit()}
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-pp-accent px-3 py-2 text-sm font-semibold text-white hover:bg-pp-accentHover disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {busy ? 'Thinking…' : 'Ask'}
      </button>

      {!organizationId && (
        <p className="mt-2 text-xs text-pp-muted">Sign in with an organization to use the assistant.</p>
      )}

      {error && (
        <p className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
          {error}
        </p>
      )}

      {result && (
        <div className="mt-3 space-y-2 border-t border-pp-line/60 pt-3">
          {result.confidence === 'low' && (
            <p className="text-[11px] font-medium text-amber-700">Low confidence — verify against official sources.</p>
          )}
          {!result.orgDocsUsed && (sources === 'both' || sources === 'org') && (
            <p className="text-[11px] text-pp-muted">Answer is general practice only (no org excerpts used).</p>
          )}
          <div className={`whitespace-pre-wrap text-sm leading-relaxed ${theme === 'dark' ? 'text-gray-100' : 'text-pp-ink'}`}>
            {result.answer}
          </div>
          {Array.isArray(result.citations) && result.citations.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-pp-muted">Sources</p>
              <ul className="mt-1 space-y-1.5">
                {result.citations.map((c, i) => (
                  <li
                    key={i}
                    className={`rounded-lg px-2 py-1.5 text-xs ${
                      theme === 'dark' ? 'bg-gray-900/80' : 'bg-pp-paper'
                    }`}
                  >
                    <span className="font-semibold">
                      {c.type === 'org'
                        ? c.documentTitle || 'Organization document'
                        : c.label || 'General parliamentary practice (RONR-style)'}
                    </span>
                    {c.snippet ? (
                      <span className={`mt-0.5 block ${theme === 'dark' ? 'text-gray-400' : 'text-pp-muted'}`}>
                        “{String(c.snippet).slice(0, 220)}
                        {String(c.snippet).length > 220 ? '…' : ''}”
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.disclaimer && (
            <p className={`text-[10px] leading-snug ${theme === 'dark' ? 'text-gray-500' : 'text-pp-muted'}`}>
              {result.disclaimer}
            </p>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
